import type { Connector } from '../shapes/Connector';
import type { Shape } from '../shapes/Shape';
import type { TextElement } from '../shapes/TextElement';
import { PADDING } from '../../../config/constants';

export interface TextEditorViewport {
  canvas: HTMLCanvasElement;
  getScale: () => number;
  getPan: () => { panX: number; panY: number };
}

export interface ShapeOverlayLayout {
  left: number;
  top: number;
  width: number;
  height: number;
  fontSize: number;
}

export interface ConnectorOverlayAnchor {
  x: number;
  y: number;
}

export interface TextElementOverlayLayout {
  left: number;
  top: number;
  width: number;
  height: number;
  fontSize: number;
}

export const getShapeOverlayLayout = (
  viewport: TextEditorViewport,
  shape: Shape
): ShapeOverlayLayout => {
  const { panX, panY } = viewport.getPan();
  const scale = viewport.getScale();
  const canvasRect = viewport.canvas.getBoundingClientRect();
  const bounds = shape.getBounds();

  return {
    left: bounds.x * scale + panX + canvasRect.left,
    top: bounds.y * scale + panY + canvasRect.top,
    width: bounds.width * scale,
    height: bounds.height * scale,
    fontSize: shape.textStyle.fontSize * scale,
  };
};

export const applyShapeOverlayLayout = (
  input: HTMLElement,
  layout: ShapeOverlayLayout
): void => {
  input.style.left = `${layout.left}px`;
  input.style.top = `${layout.top}px`;
  input.style.width = `${layout.width}px`;
  input.style.height = `${layout.height}px`;
  input.style.fontSize = `${layout.fontSize}px`;
};

export const getConnectorOverlayAnchor = (
  viewport: TextEditorViewport,
  connector: Connector
): ConnectorOverlayAnchor => {
  const { panX, panY } = viewport.getPan();
  const scale = viewport.getScale();
  const canvasRect = viewport.canvas.getBoundingClientRect();
  const textPos = connector.getTextPosition();

  return {
    x: textPos.x * scale + panX + canvasRect.left,
    y: textPos.y * scale + panY + canvasRect.top,
  };
};

export const applyConnectorOverlayPosition = (
  input: HTMLElement,
  anchor: ConnectorOverlayAnchor
): void => {
  input.style.left = `${anchor.x - 75}px`;
  input.style.top = `${anchor.y - 12}px`;
};

export const getTextElementOverlayLayout = (
  viewport: TextEditorViewport,
  textElement: TextElement,
  ctx: CanvasRenderingContext2D
): TextElementOverlayLayout => {
  const { panX, panY } = viewport.getPan();
  const scale = viewport.getScale();
  const canvasRect = viewport.canvas.getBoundingClientRect();
  const bounds = textElement.getBounds(ctx);

  // Position at viewport coordinates (scaled), but use UNSCALED dimensions/fontSize.
  // The overlay applies CSS transform: scale(scale) with transform-origin: top left.
  // This matches how the canvas ctx.scale() works, avoiding sub-pixel rounding differences
  // between pre-multiplied CSS values and the canvas transform at fractional zoom levels.
  const left = (textElement.position.x + PADDING.textElement) * scale + panX + canvasRect.left;
  const top = (textElement.position.y + PADDING.textElement) * scale + panY + canvasRect.top;

  if (textElement.maxWidth) {
    const availableWidth = textElement.maxWidth - PADDING.textElement * 2;
    return {
      left,
      top,
      width: availableWidth,
      height: Math.max(50 / scale, bounds.height + 20 / scale),
      fontSize: textElement.textStyle.fontSize,
    };
  }

  // Use generous minimum dimensions (in scene units)
  const minWidth = 300 / scale;
  const minHeight = 50 / scale;
  const bufferWidth = 60 / scale;
  const bufferHeight = 30 / scale;

  return {
    left,
    top,
    width: Math.max(minWidth, bounds.width + bufferWidth),
    height: Math.max(minHeight, bounds.height + bufferHeight),
    fontSize: textElement.textStyle.fontSize,
  };
};
